/**
 * Shared Notification Display Data Extractor
 *
 * Parses the raw discriminated `Notification.data` payload into structured
 * view-model objects consumed by both web and mobile notification card
 * components.
 */

import type { LeagueKey } from './league';
import type { Notification, NotificationType, SettlementLegSummary } from './notification';
import { formatAmericanOdds } from './oddsUtils';
import { formatStatLabel } from './statLabels';
import { gameChartPropType } from './statType';

// Re-export for consumers that imported from here
export { formatStatLabel } from './statLabels';

// =============================================================================
// Types
// =============================================================================

export type NotificationCardVariant = 'settlement' | 'leg' | 'social' | 'monitoring' | 'system';

export interface ParsedLegInfo {
  playerName: string;
  team: string;
  opponentTeam: string;
  overUnder: string;
  line: string;
  /** Raw stat type code from the notification (e.g. "player_points_rebounds_assists") */
  statType: string;
  /** Human-readable stat label (e.g. "PRA") */
  statLabel: string;
  actualValue: string;
  result: 'hit' | 'lost';
  /** True for spread/total/moneyline props (no player) */
  isGameLevel: boolean;
  /** Full prop display string for game-level props (e.g. "San Antonio Spurs -7.5") */
  prop: string;
  /**
   * League the leg belongs to. Added by the extractor (not by parseLegSummary,
   * which is summary-string-only). Renderers use this for getTeamAbbr /
   * getTeamLogo lookups without reaching back into `notification.data`.
   */
  leagueKey: LeagueKey;
  /**
   * Raw American odds string for the leg, if present. Display formatting is
   * the renderer's job — pass through `formatOddsDisplay`.
   */
  odds?: string;
}

/** Display-ready settlement leg (parsed from SettlementLegSummary) */
export interface ParsedSettlementLeg {
  player: string;
  team: string;
  /** Human-readable stat label (e.g. "Points", "PRA") */
  statLabel: string;
  /** Raw stat type code */
  statType: string;
  line: string;
  overUnder: string;
  result: 'hit' | 'miss' | 'void' | 'push';
  actualValue: string;
}

export interface ParsedSettlementInfo {
  legCount: number;
  oddsFormatted: string;
  result: 'won' | 'lost' | 'pushed' | 'voided';
  dnpNames: string[];
  legs: ParsedSettlementLeg[];
}

export interface ParsedSocialInfo {
  actorDisplayName: string;
  actorInitials: string;
  actorAvatarUrl: string;
  oddsFormatted: string;
}

export interface ParsedMonitoringInfo {
  /** 'line_movement' | 'sharp_signal' | 'injury' */
  monitoringType: 'line_movement' | 'sharp_signal' | 'injury';
  /** Player or prop affected */
  player: string;
  /**
   * League the alert belongs to. Renderers use this for getTeamAbbr /
   * getTeamLogo lookups without reaching back into `notification.data`.
   */
  leagueKey: LeagueKey;
  /** Team abbreviation (for headshot badge) */
  team?: string;
  /** Line movement amount (for line_movement type) */
  lineMovement?: number;
  /** Current market line (for line_movement type) */
  currentLine?: number;
  /** Opening line (for line_movement type) */
  openingLine?: number;
  /** Sharp signal type (for sharp_signal type) */
  sharpSignalType?: 'rlm' | 'positive_ev' | 'consensus' | 'sharp_line';
  /** Human-readable stat label (e.g. "Points", "Rebounds") */
  statLabel?: string;
  /** Over/Under direction */
  overUnder?: string;
  /** Line value */
  line?: number;
  /** EV percentage edge (for positive_ev sharp signals) */
  evPercent?: number;
  /** Injury status (for injury type) */
  injuryStatus?: 'Out' | 'Out For Season' | 'Questionable' | 'Doubtful';
  /** Previous injury status (for injury type) */
  previousInjuryStatus?: 'Out' | 'Out For Season' | 'Questionable' | 'Doubtful';
}

export type NotificationColorScheme = 'success' | 'danger' | 'warning' | 'primary' | 'muted';

export interface NotificationDisplayData {
  variant: NotificationCardVariant;
  leg?: ParsedLegInfo;
  settlement?: ParsedSettlementInfo;
  social?: ParsedSocialInfo;
  monitoring?: ParsedMonitoringInfo;
  hasNavigation: boolean;
  /** Linked parlay id for navigation (when present). */
  parlayId?: number;
  colorScheme: NotificationColorScheme;
  /** Type-specific label (e.g. "Bet Won", "Leg Hit") */
  label: string;
}

// =============================================================================
// Parsing Functions
// =============================================================================

/**
 * Parse leg summary: "Leg hit: LeBron James Over 25.5 pts (actual: 31)"
 * Returns parsed info or null if format doesn't match.
 *
 * Returns `Omit<ParsedLegInfo, 'leagueKey'>` because the league is not
 * derivable from the summary string — the extractor adds it from the
 * notification payload before handing the parsed leg to the renderer.
 */
export function parseLegSummary(
  summary: string,
  player: string | undefined,
  type: NotificationType,
  team?: string,
  opponentTeam?: string,
  statType?: string,
  prop?: string,
): Omit<ParsedLegInfo, 'leagueKey'> | null {
  const result = type === 'parlay_leg_hit' ? 'hit' : 'lost';
  const playerName = player ?? '';
  const teamStr = team ?? '';
  const opponentStr = opponentTeam ?? '';
  const isGameLevel = !!(statType && gameChartPropType(statType));

  // Try structured regex: "Leg hit: Player Over 25.5 pts (actual: 31)"
  const match = summary.match(
    /Leg (?:hit|lost): .+? (Over|Under) ([\d.]+) (.+?)(?:\s*\(actual:\s*([^)]+)\))?$/i,
  );

  if (match) {
    const rawStat = statType || match[3].trim();
    return {
      playerName:
        playerName || summary.match(/Leg (?:hit|lost): (.+?) (?:Over|Under)/i)?.[1] || 'Unknown',
      team: teamStr,
      opponentTeam: opponentStr,
      overUnder: match[1],
      line: match[2],
      statType: rawStat,
      statLabel: formatStatLabel(rawStat),
      actualValue: match[4]?.trim() ?? '?',
      result,
      isGameLevel,
      prop: prop ?? '',
    };
  }

  // Fallback: try moneyline / no-line format: "Leg hit: Player moneyline (actual: 27)"
  const fallbackMatch = summary.match(/Leg (?:hit|lost): .+? (.+?)(?:\s*\(actual:\s*([^)]+)\))?$/i);

  if (playerName || isGameLevel) {
    const rawStat = statType || fallbackMatch?.[1]?.trim() || '';
    return {
      playerName: isGameLevel ? prop || teamStr || playerName : playerName,
      team: teamStr,
      opponentTeam: opponentStr,
      overUnder: '',
      line: '',
      statType: rawStat,
      statLabel: rawStat ? formatStatLabel(rawStat) : '',
      actualValue: fallbackMatch?.[2]?.trim() ?? '',
      result,
      isGameLevel,
      prop: prop ?? '',
    };
  }

  return null;
}

/**
 * Parse settlement summary: "Your 3-leg parlay won! (+425)"
 * Returns leg count or 0 if unparseable.
 */
export function parseSettlementLegCount(summary: string): number {
  const match = summary.match(/(\d+)-leg/);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Parse DNP player names from voided summary:
 * "Parlay voided — LeBron James, Anthony Davis DNP"
 */
export function parseDnpNames(summary: string): string[] {
  const dashIdx = summary.indexOf('—');
  if (dashIdx === -1) return [];
  const afterDash = summary
    .slice(dashIdx + 1)
    .replace(/\s*DNP\s*$/i, '')
    .trim();
  if (!afterDash) return [];
  return afterDash
    .split(',')
    .map((n) => n.trim())
    .filter(Boolean);
}

/**
 * Format odds for display. Ensures "+" prefix for positive odds.
 * Returns empty string if no odds.
 */
export function formatOddsDisplay(odds: string | undefined): string {
  return formatAmericanOdds(odds) ?? '';
}

/**
 * Get initials from a display name.
 * "John Doe" → "JD", "alice" → "AL"
 */
export function getInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '??';
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

/**
 * Generate a ui-avatars.com URL for a user display name.
 */
export function getActorAvatarUrl(name: string): string {
  const encoded = encodeURIComponent(name);
  return `https://ui-avatars.com/api/?name=${encoded}&background=3b82f6&color=fff&size=80&bold=true`;
}

// =============================================================================
// Label + Color Mapping
// =============================================================================

const TYPE_LABELS: Record<NotificationType, string> = {
  bet_won: 'Bet Won',
  bet_lost: 'Bet Lost',
  bet_pushed: 'Bet Pushed',
  bet_voided: 'Bet Voided',
  parlay_leg_hit: 'Leg Hit',
  parlay_leg_lost: 'Leg Miss',
  game_starting: 'Game Starting',
  progress_milestone: 'Milestone',
  line_crossed: 'Line Crossed',
  leg_busted: 'Leg Busted',
  halftime_summary: 'Halftime',
  followed_user_bet: 'New Bet',
  followed_user_settled: 'Settled',
  parlay_shared_with_you: 'Parlay Received',
  parlay_shared_by_you: 'Parlay Shared',
  friend_request_received: 'Friend Request',
  friend_request_accepted: 'Friend Added',
  new_follower: 'New Follower',
  subscription_upgraded: 'Upgraded',
  subscription_downgraded: 'Downgraded',
  subscription_cancelled: 'Cancelled',
  subscription_payment_failed: 'Payment Failed',
  subscription_expiring: 'Expiring',
  subscription_renewed: 'Renewed',
  subscription_dispute: 'Dispute Filed',
  subscription_dispute_won: 'Dispute Won',
  subscription_dispute_lost: 'Dispute Lost',
  subscription_dispute_banned: 'Account Suspended',
  welcome: 'Welcome',
  creator_connect_reminder: 'Connect Stripe',
  creator_eligible: 'Creator Status',
  creator_payout_sent: 'Payout Sent',
  creator_payout_failed: 'Payout Failed',
  account_linked: 'Account Linked',
  account_unlinked: 'Account Unlinked',
  email_changed: 'Email Changed',
  password_changed: 'Password Changed',
  daily_summary: 'Daily Summary',
  weekly_recap: 'Weekly Recap',
  streak_alert: 'Streak Alert',
  quota_warning: 'Quota Warning',
  calibration_needed: 'Calibration Needed',
  service_health_alert: 'Service Alert',
  daily_gen_truncated: 'Daily Gen Alert',
  line_movement_alert: 'Line Movement',
  sharp_signal_alert: 'Sharp Signal',
  injury_alert: 'Injury Alert',
  slate_injury_alert: 'Slate Injury',
  slate_line_movement_alert: 'Slate Line Move',
  slate_sharp_signal_alert: 'Slate Sharp Signal',
  edge_proven_above: "You're beating the AI",
  edge_proven_below: 'Your bets trail the AI',
  edge_no_longer_proven: 'Your edge is no longer proven',
};

const TYPE_COLORS: Record<NotificationType, NotificationColorScheme> = {
  bet_won: 'success',
  bet_lost: 'danger',
  bet_pushed: 'warning',
  bet_voided: 'warning',
  parlay_leg_hit: 'success',
  parlay_leg_lost: 'danger',
  game_starting: 'warning',
  progress_milestone: 'primary',
  line_crossed: 'primary',
  leg_busted: 'danger',
  halftime_summary: 'warning',
  followed_user_bet: 'primary',
  followed_user_settled: 'primary',
  parlay_shared_with_you: 'primary',
  parlay_shared_by_you: 'primary',
  friend_request_received: 'primary',
  friend_request_accepted: 'success',
  new_follower: 'primary',
  subscription_upgraded: 'success',
  subscription_downgraded: 'warning',
  subscription_cancelled: 'warning',
  subscription_payment_failed: 'danger',
  subscription_expiring: 'warning',
  subscription_renewed: 'success',
  subscription_dispute: 'danger',
  subscription_dispute_won: 'success',
  subscription_dispute_lost: 'danger',
  subscription_dispute_banned: 'danger',
  welcome: 'success',
  creator_connect_reminder: 'primary',
  creator_eligible: 'success',
  creator_payout_sent: 'success',
  creator_payout_failed: 'danger',
  account_linked: 'success',
  account_unlinked: 'warning',
  email_changed: 'warning',
  password_changed: 'warning',
  daily_summary: 'muted',
  weekly_recap: 'muted',
  streak_alert: 'warning',
  quota_warning: 'warning',
  calibration_needed: 'warning',
  service_health_alert: 'danger',
  daily_gen_truncated: 'danger',
  line_movement_alert: 'warning',
  sharp_signal_alert: 'primary',
  injury_alert: 'danger',
  slate_injury_alert: 'danger',
  slate_line_movement_alert: 'warning',
  slate_sharp_signal_alert: 'primary',
  edge_proven_above: 'success',
  edge_proven_below: 'warning',
  edge_no_longer_proven: 'muted',
};

export function getNotificationLabel(type: NotificationType): string {
  return (
    TYPE_LABELS[type] ??
    String(type)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function getNotificationColorScheme(type: NotificationType): NotificationColorScheme {
  return TYPE_COLORS[type] ?? 'muted';
}

// =============================================================================
// Variant Classification
// =============================================================================

const SETTLEMENT_TYPES = new Set<NotificationType>([
  'bet_won',
  'bet_lost',
  'bet_pushed',
  'bet_voided',
]);

const LEG_TYPES = new Set<NotificationType>(['parlay_leg_hit', 'parlay_leg_lost']);

const SOCIAL_TYPES = new Set<NotificationType>([
  'followed_user_bet',
  'followed_user_settled',
  'parlay_shared_with_you',
  'parlay_shared_by_you',
  'friend_request_received',
  'friend_request_accepted',
  'new_follower',
]);

const MONITORING_TYPES = new Set<NotificationType>([
  'line_movement_alert',
  'sharp_signal_alert',
  'injury_alert',
  'slate_injury_alert',
  'slate_line_movement_alert',
  'slate_sharp_signal_alert',
]);

function getVariant(type: NotificationType): NotificationCardVariant {
  if (LEG_TYPES.has(type)) return 'leg';
  if (SETTLEMENT_TYPES.has(type)) return 'settlement';
  if (SOCIAL_TYPES.has(type)) return 'social';
  if (MONITORING_TYPES.has(type)) return 'monitoring';
  return 'system';
}

// =============================================================================
// Master Extractor
// =============================================================================

/**
 * Notification types that carry a `parlayId` for navigation (Leg, Settlement,
 * LiveGame, Monitoring, plus the followed/share Social variants).
 * Account/System branches have nothing to navigate to.
 */
const TYPES_WITH_PARLAY_ID: ReadonlySet<NotificationType> = new Set<NotificationType>([
  'parlay_leg_hit',
  'parlay_leg_lost',
  'bet_won',
  'bet_lost',
  'bet_pushed',
  'bet_voided',
  'game_starting',
  'progress_milestone',
  'line_crossed',
  'leg_busted',
  'halftime_summary',
  'followed_user_bet',
  'followed_user_settled',
  'parlay_shared_with_you',
  'parlay_shared_by_you',
  'line_movement_alert',
  'sharp_signal_alert',
  'injury_alert',
  'slate_injury_alert',
  'slate_line_movement_alert',
  'slate_sharp_signal_alert',
]);

/**
 * Read the navigation `parlayId` from a notification regardless of branch.
 * Account/System branches never carry one. Returns undefined for those.
 */
function getParlayIdForNav(notification: Notification): number | undefined {
  if (!TYPES_WITH_PARLAY_ID.has(notification.type)) return undefined;
  // Every branch in TYPES_WITH_PARLAY_ID exposes parlayId (required or
  // optional) on its data shape; a structural read is safe.
  const data = notification.data as { parlayId?: number };
  return data.parlayId;
}

/**
 * Extract structured display data from a notification.
 * Both web and mobile card components consume this output.
 *
 * Switches on `notification.type` so TS narrows `notification.data` to its
 * matching branch (LegNotificationData / SettlementNotificationData / etc.)
 * inside each case — no `as` casts, no field access on union members.
 */
export function extractNotificationDisplayData(
  notification: Notification,
): NotificationDisplayData {
  const variant = getVariant(notification.type);
  const colorScheme = getNotificationColorScheme(notification.type);
  const label = getNotificationLabel(notification.type);
  const parlayId = getParlayIdForNav(notification);
  const hasNavigation = parlayId != null;

  const base: NotificationDisplayData = {
    variant,
    colorScheme,
    label,
    hasNavigation,
    parlayId,
  };

  switch (notification.type) {
    case 'parlay_leg_hit':
    case 'parlay_leg_lost': {
      // notification.data narrows to LegNotificationData
      const { data } = notification;
      const parsed = parseLegSummary(
        data.summary,
        data.player,
        notification.type,
        data.team,
        data.opponentTeam,
        data.statType,
        data.prop,
      );
      if (!parsed) return base;
      const leg: ParsedLegInfo = {
        ...parsed,
        leagueKey: data.leagueKey,
        odds: data.odds,
      };
      return { ...base, leg };
    }

    case 'bet_won':
    case 'bet_lost':
    case 'bet_pushed':
    case 'bet_voided': {
      // notification.data narrows to SettlementNotificationData
      const { data } = notification;
      const resultMap: Record<typeof notification.type, ParsedSettlementInfo['result']> = {
        bet_won: 'won',
        bet_lost: 'lost',
        bet_pushed: 'pushed',
        bet_voided: 'voided',
      };
      const parsedLegs: ParsedSettlementLeg[] = data.legs.map((leg: SettlementLegSummary) => ({
        player: leg.player,
        team: leg.team ?? '',
        statType: leg.statType,
        statLabel: leg.statType ? formatStatLabel(leg.statType) : '',
        line: leg.line != null ? String(leg.line) : '',
        overUnder: leg.overUnder ?? '',
        result: leg.result,
        actualValue: leg.actualValue != null ? String(leg.actualValue) : '',
      }));
      const settlement: ParsedSettlementInfo = {
        legCount: parseSettlementLegCount(data.summary),
        oddsFormatted: formatOddsDisplay(data.odds),
        result: resultMap[notification.type],
        dnpNames: notification.type === 'bet_voided' ? parseDnpNames(data.summary) : [],
        legs: parsedLegs,
      };
      return { ...base, settlement };
    }

    case 'followed_user_bet':
    case 'followed_user_settled':
    case 'parlay_shared_with_you':
    case 'parlay_shared_by_you':
    case 'friend_request_received':
    case 'friend_request_accepted':
    case 'new_follower': {
      // notification.data narrows to SocialNotificationData
      const { data } = notification;
      const actorName = data.actorDisplayName ?? 'Someone';
      const social: ParsedSocialInfo = {
        actorDisplayName: actorName,
        actorInitials: getInitials(actorName),
        actorAvatarUrl: getActorAvatarUrl(actorName),
        oddsFormatted: formatOddsDisplay(data.odds),
      };
      return { ...base, social };
    }

    case 'line_movement_alert':
    case 'sharp_signal_alert':
    case 'injury_alert':
    case 'slate_injury_alert':
    case 'slate_line_movement_alert':
    case 'slate_sharp_signal_alert': {
      // notification.data narrows to MonitoringNotificationData
      const { data } = notification;
      const monitoringType =
        notification.type === 'injury_alert' || notification.type === 'slate_injury_alert'
          ? ('injury' as const)
          : notification.type === 'sharp_signal_alert' ||
              notification.type === 'slate_sharp_signal_alert'
            ? ('sharp_signal' as const)
            : ('line_movement' as const);

      const monitoring: ParsedMonitoringInfo = {
        monitoringType,
        player: data.player ?? 'Unknown',
        leagueKey: data.leagueKey,
        team: data.team,
        lineMovement: data.lineMovement,
        currentLine: data.currentLine,
        openingLine: data.openingLine,
        sharpSignalType: data.sharpSignalType,
        evPercent: data.evPercent,
        statLabel: data.statType ? formatStatLabel(data.statType) : undefined,
        overUnder: data.overUnder,
        line: data.currentLine ?? data.openingLine,
        injuryStatus: data.injuryStatus,
        previousInjuryStatus: data.previousInjuryStatus,
      };
      return { ...base, monitoring };
    }

    default:
      // LiveGame, Account, and System variants render with just `base`.
      return base;
  }
}
